import React, { ReactElement, ReactNode } from 'react';
import { Layout as AntLayout } from 'antd';
import styled from 'styled-components';
import { colorsPalette } from '../../styles/colors-palette';
import { GRID_SIZES, scrollbar } from '../../styles/GlobalStyle';

interface SideLayoutProps {
  children?: ReactNode;
  className?: string;
}

function SideLayout({ children, className }: SideLayoutProps): ReactElement {
  return <StyledLayout className={className}>{children}</StyledLayout>;
}

interface SideLayoutHeaderProps {
  children?: ReactNode;
  className?: string;
}

function SideLayoutHeader({ children, className }: SideLayoutHeaderProps): ReactElement {
  return <StyledHeader className={className}>{children}</StyledHeader>;
}

interface SideLayoutSiderProps {
  children?: ReactNode;
  width?: number;
  collapsed?: boolean;
  breakpoint?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl';
  onBreakpoint?: (broken: boolean) => void;
  className?: string;
}

function SideLayoutSider({
  children,
  width = 280,
  collapsed = false,
  breakpoint,
  onBreakpoint,
  className,
}: SideLayoutSiderProps): ReactElement {
  return (
    <StyledSider
      width={width}
      collapsed={collapsed}
      collapsedWidth={0}
      trigger={null}
      breakpoint={breakpoint}
      onBreakpoint={onBreakpoint}
      className={className}
    >
      {children}
    </StyledSider>
  );
}

interface SideLayoutContentProps {
  children?: ReactNode;
  className?: string;
}

function SideLayoutContent({ children, className }: SideLayoutContentProps): ReactElement {
  return <StyledContent className={className}>{children}</StyledContent>;
}

const StyledLayout = styled(AntLayout)`
  height: 100vh;
  min-width: ${GRID_SIZES.content}px;
  background: ${colorsPalette.lightGray250};
  flex-direction: row !important;
  overflow: hidden;
`;

const StyledHeader = styled(AntLayout.Header)`
  &.ant-layout-header {
    display: flex;
    align-items: center;
    height: 64px;
    padding: 0 16px;
    background: ${colorsPalette.lightGray250};
    gap: 16px;
    line-height: normal;
    flex-shrink: 0;
  }
`;

const StyledSider = styled(AntLayout.Sider)`
  &.ant-layout-sider {
    background: ${colorsPalette.lightGray250};
    overflow-y: auto;
    overflow-x: hidden;
    height: 100vh;
    flex-shrink: 0;
    ${scrollbar};
  }
`;

const StyledContent = styled(AntLayout.Content)`
  &.ant-layout-content {
    background: ${colorsPalette.white};
    border-radius: 24px;
    flex: 1;
    min-width: ${GRID_SIZES.content}px;
    padding: 20px;
    margin: 0 8px 8px 0;
    overflow-y: auto;
    overflow-x: hidden;
    ${scrollbar};
  }
`;

SideLayout.Header = SideLayoutHeader;
SideLayout.Sider = SideLayoutSider;
SideLayout.Content = SideLayoutContent;

export {
  SideLayout,
  SideLayoutHeader,
  SideLayoutSider,
  SideLayoutContent,
  type SideLayoutProps,
  type SideLayoutHeaderProps,
  type SideLayoutSiderProps,
  type SideLayoutContentProps,
};
